import { FC, Suspense } from "react";
import { Asset, Network, THOR, TxResult, networkTxLink } from "rujira.js";
import {
  Animated,
  AssetOverviewActions,
  Button,
  IconDenom,
  Icons,
  getTransferTime,
  useTranslation,
} from "rujira.ui";
import { QuietErrorBoundary } from "./QuietErrorBoundary";
import {
  TransferSuccessRecommendation,
  useTransferSuccessRecommendations,
} from "./TransferSuccessRecommendations";

export type TransferSuccessAction = "deposit" | "send" | "withdraw";

type Translate = ReturnType<typeof useTranslation>["t"];

type TransferSuccessProps = {
  action: TransferSuccessAction;
  asset: Asset;
  dismiss: () => void;
} & (
  | {
      tx: TxResult;
      arrivalNetwork?: Network;
      completionVariant?: never;
    }
  | {
      tx?: never;
      arrivalNetwork?: never;
      completionVariant: "exchange";
    }
);

export const TransferSuccess: FC<TransferSuccessProps> = ({
  action,
  asset,
  tx,
  dismiss,
  arrivalNetwork,
  completionVariant,
}) => {
  const { t } = useTranslation("common");
  const symbol = asset.metadata.symbol;
  const exchangeCompletion = completionVariant === "exchange";
  const transactionLink = tx ? networkTxLink(tx) : undefined;
  const trackerLink =
    action === "deposit" && tx
      ? networkTxLink({
          network: THOR,
          txHash: tx.txHash.replace(/^0x/, "").toUpperCase(),
        })
      : undefined;
  const estimatedTime = formatEstimatedTime(
    getTransferTime(arrivalNetwork || tx?.network || asset.chain),
    t("seconds"),
    t("minutes"),
    t("na")
  );

  return (
    <div className="transfer-modal transfer-success">
      <header className="transfer-modal__header">
        <h3 className="h3 flex ai-c mb-0">
          <IconDenom denom={symbol} className="as-c block w-4 h-4 mr-1" />
          {exchangeCompletion
            ? t("exchangeDepositCompleteTitle")
            : progressTitle(t, action, symbol)}
        </h3>
        <p className="transfer-modal__description">
          {exchangeCompletion
            ? t("exchangeDepositCompleteDescription", { symbol })
            : progressDescription(t, action, symbol)}
        </p>
      </header>

      <section className="transfer-success__status mt-2 flex ai-c br-2 bg-teal-10">
        <Animated
          name="tx-success"
          color="teal"
          loop={false}
          className="transfer-success__status-icon color-teal block no-shrink"
        />
        <span className="transfer-success__status-copy color-teal">
          <span>
            {exchangeCompletion
              ? t("exchangeDepositCompleteStatusTitle")
              : t("transactionSubmitted")}
          </span>
          <small>
            {exchangeCompletion
              ? t("exchangeDepositCompleteStatus", { symbol })
              : progressStatus(t, action, symbol, estimatedTime)}
          </small>
        </span>
      </section>

      {transactionLink && (
        <div className="transfer-success__links">
          <a
            href={transactionLink}
            target="_blank"
            rel="noreferrer"
            className="color-grey hover-primary1 no-underline">
            {t("viewTransaction")}
            <Icons.External className="inline-block w-1.5 h-1.5 ml-0.5" />
          </a>
          {trackerLink && (
            <a
              href={trackerLink}
              target="_blank"
              rel="noreferrer"
              className="color-grey hover-primary1 no-underline">
              {t("trackDeposit")}
              <Icons.External className="inline-block w-1.5 h-1.5 ml-0.5" />
            </a>
          )}
        </div>
      )}

      {/* Optional content: a failed lookup must never affect the receipt above. */}
      <QuietErrorBoundary>
        <section className="transfer-success__discover">
          <h4 className="h4 mb-0">{t("transactionExploreTitle")}</h4>
          <p className="transfer-modal__description mt-0.5">
            {t("transactionExploreDescription", { symbol })}
          </p>
          <Suspense fallback={<div className="skeleton h-8 br-2 mt-1" />}>
            <RecommendationActions asset={asset} dismiss={dismiss} t={t} />
          </Suspense>
        </section>
      </QuietErrorBoundary>

      <footer className="transfer-success__footer">
        <Button label={t("continueExploring")} onClick={dismiss} />
      </footer>
    </div>
  );
};

const RecommendationActions: FC<{
  asset: Asset;
  dismiss: () => void;
  t: Translate;
}> = ({ asset, dismiss, t }) => {
  const recommendations = useTransferSuccessRecommendations(asset, t);
  const actions = recommendations.map((item) => {
    const Icon = recommendationIcon(item.kind);
    return {
      href: item.href,
      icon: <Icon />,
      title: (
        <>
          {item.title}
          {item.apr && (
            <span className="color-teal">
              {" \u00b7 "}
              {recommendationApr(t, item)}
            </span>
          )}
        </>
      ),
      description: item.description,
    };
  });

  return (
    <AssetOverviewActions
      actions={actions}
      divided={false}
      className="mt-1"
      onNavigate={dismiss}
    />
  );
};

const recommendationApr = (
  t: Translate,
  item: TransferSuccessRecommendation
) =>
  item.aprLabel === "upTo"
    ? t("transactionExploreAprUpTo", { apr: item.apr })
    : item.aprLabel === "borrow"
      ? t("transactionExploreBorrowApr", { apr: item.apr })
      : t("transactionExploreCurrentApr", { apr: item.apr });

const recommendationIcon = (kind: TransferSuccessRecommendation["kind"]) => {
  switch (kind) {
    case "automated":
      return Icons.Binary;
    case "lending":
      return Icons.Lend;
    case "borrow":
      return Icons.MoneyTransfer;
    case "trade":
      return Icons.Swap;
    case "staking":
    case "ruji":
      return Icons.Seedling;
  }
};

const progressTitle = (
  t: Translate,
  action: TransferSuccessAction,
  symbol: string
) => {
  switch (action) {
    case "deposit":
      return t("depositInProgress", { symbol });
    case "send":
      return t("sendInProgress", { symbol });
    case "withdraw":
      return t("withdrawInProgress", { symbol });
  }
};

const progressDescription = (
  t: Translate,
  action: TransferSuccessAction,
  symbol: string
) => {
  switch (action) {
    case "deposit":
      return t("depositProgressDescription", { symbol });
    case "send":
      return t("sendProgressDescription", { symbol });
    case "withdraw":
      return t("withdrawProgressDescription", { symbol });
  }
};

const progressStatus = (
  t: Translate,
  action: TransferSuccessAction,
  symbol: string,
  time: string
) => {
  switch (action) {
    case "deposit":
      return t("depositWaitingConfirmation", { symbol, time });
    case "send":
      return t("sendWaitingConfirmation", { symbol, time });
    case "withdraw":
      return t("withdrawWaitingConfirmation", { symbol, time });
  }
};

const formatEstimatedTime = (
  value: string | undefined,
  secondsLabel: string,
  minutesLabel: string,
  unavailableLabel: string
) =>
  value
    ? value
        .replace(/\bseconds\b/g, secondsLabel)
        .replace(/\bminutes\b/g, minutesLabel)
    : unavailableLabel;
